import { useState } from "react";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { Loader2, Mail, Plus, Send, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Hint, KeyHint } from "../hints";
import { StepCard, StepCardLabel } from "../step-card";
import { chipVariants, motionSafe, staggerContainer } from "../motion";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * Step 5 — invite teammates. Optional by design: it's the one step that
 * depends on someone other than the user, so blocking on it would strand
 * anyone setting up solo.
 */
export function InviteStep({
  onSubmit,
  onSkip,
  isSubmitting,
}: {
  onSubmit: (emails: string[]) => void;
  onSkip: () => void;
  isSubmitting: boolean;
}) {
  const shouldReduce = useReducedMotion();
  const [emails, setEmails] = useState<string[]>([]);
  const [draft, setDraft] = useState("");
  const [error, setError] = useState<string | null>(null);

  const add = () => {
    const value = draft.trim().toLowerCase();
    if (!value) return;

    if (!EMAIL_RE.test(value)) {
      setError("That doesn't look like an email address.");
      return;
    }
    if (emails.includes(value)) {
      setError("Already on the list.");
      return;
    }

    setEmails((prev) => [...prev, value]);
    setDraft("");
    setError(null);
  };

  return (
    <StepCard>
      <div className="space-y-3">
        <StepCardLabel>Teammates</StepCardLabel>

        <AnimatePresence>
          {emails.length > 0 && (
            <motion.div
              className="flex flex-wrap gap-1.5"
              variants={motionSafe(staggerContainer, shouldReduce)}
              initial="hidden"
              animate="visible"
              exit={{ opacity: 0 }}
            >
              <AnimatePresence mode="popLayout">
                {emails.map((email) => (
                  <motion.span
                    key={email}
                    layout
                    variants={motionSafe(chipVariants, shouldReduce)}
                    exit="exit"
                    className="inline-flex items-center gap-1 rounded-full border border-primary/25 bg-primary/8 py-1 pl-2.5 pr-1.5 text-xs font-medium"
                  >
                    <Mail className="h-3 w-3 text-muted-foreground" />
                    {email}
                    <button
                      type="button"
                      onClick={() => setEmails((prev) => prev.filter((e) => e !== email))}
                      aria-label={`Remove ${email}`}
                      className="rounded-full p-0.5 text-muted-foreground/60 transition-colors hover:bg-primary/15 hover:text-foreground"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </motion.span>
                ))}
              </AnimatePresence>
            </motion.div>
          )}
        </AnimatePresence>

        <div className="flex gap-2">
          <Input
            value={draft}
            onChange={(e) => {
              setDraft(e.target.value);
              setError(null);
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === ",") {
                e.preventDefault();
                add();
              }
            }}
            placeholder="colleague@yourfund.com"
            type="email"
            disabled={isSubmitting}
            className="h-9 text-sm"
          />
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={add}
            disabled={!draft.trim() || isSubmitting}
            className="h-9 shrink-0 gap-1.5"
          >
            <Plus className="h-3.5 w-3.5" />
            Add
          </Button>
        </div>

        {error ? (
          <Hint className="text-destructive">{error}</Hint>
        ) : (
          <Hint>
            Press <KeyHint keys={["↵"]} className="inline-flex align-middle" /> after each address. Everyone
            joins as a Partner; you can change roles later in Team settings.
          </Hint>
        )}

        <div className="flex items-center justify-between gap-3 pt-1">
          <button
            type="button"
            onClick={onSkip}
            disabled={isSubmitting}
            className="text-xs text-muted-foreground underline-offset-4 transition-colors hover:text-foreground hover:underline"
          >
            I'll do this later
          </button>

          <Button
            onClick={() => onSubmit(emails)}
            disabled={emails.length === 0 || isSubmitting}
            className="gap-2"
          >
            {isSubmitting ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <>
                <Send className="h-4 w-4" />
                Send {emails.length > 0 ? `${emails.length} invite${emails.length > 1 ? "s" : ""}` : "invites"}
              </>
            )}
          </Button>
        </div>
      </div>
    </StepCard>
  );
}
